import { useRef, useState } from "react";
import { ROLE_OPTIONS, type RoleValue } from "../setup-flow-config";

interface Props {
    onSelect: (role: RoleValue) => void;
    /** Committed choice when the user returns via Back (drives aria-checked). */
    value?: RoleValue | null;
}

function initialTabStopIndex(saved: RoleValue | null | undefined): number {
    if (saved) {
        const idx = ROLE_OPTIONS.findIndex((r) => r.value === saved);
        if (idx >= 0) return idx;
    }
    return 0;
}

export default function RoleStep({ onSelect, value = null }: Props) {
    const [tabStopIndex, setTabStopIndex] = useState(() => initialTabStopIndex(value));
    const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const optionCount = ROLE_OPTIONS.length;

    return (
        <div className="flex w-full max-w-[520px] flex-col items-center gap-10">
            <h1 id="role-step-heading" className="fraunces-soft text-4xl font-semibold leading-tight text-shadow-700">
                What's your role?
            </h1>

            <div
                className="flex w-full flex-col gap-3"
                role="radiogroup"
                aria-labelledby="role-step-heading"
                aria-describedby="role-step-hint"
                aria-orientation="vertical"
            >
                {ROLE_OPTIONS.map((role, i) => (
                    <button
                        key={role.value}
                        type="button"
                        role="radio"
                        aria-checked={value === role.value}
                        aria-posinset={i + 1}
                        aria-setsize={optionCount}
                        ref={(el) => {
                            buttonRefs.current[i] = el;
                        }}
                        tabIndex={tabStopIndex === i ? 0 : -1}
                        onFocus={() => setTabStopIndex(i)}
                        onClick={() => onSelect(role.value)}
                        onKeyDown={(e) => {
                            if (e.key === "ArrowDown") {
                                e.preventDefault();
                                const next = (i + 1) % ROLE_OPTIONS.length;
                                buttonRefs.current[next]?.focus();
                            } else if (e.key === "ArrowUp") {
                                e.preventDefault();
                                const prev = (i - 1 + ROLE_OPTIONS.length) % ROLE_OPTIONS.length;
                                buttonRefs.current[prev]?.focus();
                            } else if (e.key === "Home") {
                                e.preventDefault();
                                buttonRefs.current[0]?.focus();
                            } else if (e.key === "End") {
                                e.preventDefault();
                                buttonRefs.current[optionCount - 1]?.focus();
                            }
                        }}
                        className="rounded-xl border border-shadow-100 bg-white px-6 py-3.5 text-lg font-medium text-shadow-500 transition-colors focused-inset hover:bg-shadow-25 focus-visible:bg-max-50 focus-visible:text-max-800"
                    >
                        {role.label}
                    </button>
                ))}
            </div>

            <p id="role-step-hint" className="text-sm text-shadow-400">
                Use{" "}
                <kbd className="rounded border border-shadow-100 bg-shadow-25 px-1.5 py-0.5 font-mono text-xs">↑↓</kbd>{" "}
                then{" "}
                <kbd className="rounded border border-shadow-100 bg-shadow-25 px-1.5 py-0.5 font-mono text-xs">
                    Enter ↵
                </kbd>
            </p>
        </div>
    );
}
